// Inbox — everything that needs a person, ranked (Part B, 2026-09-25).
//
// The manager's home. Goal from the brief: see the problem and act in two
// clicks without reading a chat. Each row says WHO, WHAT is wrong in plain
// words, SINCE WHEN, and the money at stake; the panel offers the one action
// that fixes it.
//
// ⚠️ RED MEANS « NEEDS A HUMAN NOW » AND NOTHING ELSE. Amber is today, grey is
// watch. Colour is never alone: every tier also has its words.
//
// Every button asks the one permission check (`useCan`, web/src/lib/actions.ts).
// Keys: J/K move · E handled · # ignore · S snooze · Enter open chat · Z undo ·
// ? all shortcuts · Esc close. See workbench.ts.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { previewPlanText, planIsHeld, type PreviewPlanLike } from '@/lib/sendVisibility';
import { useT } from '@/lib/i18n';
import { useCan } from '@/lib/actions';
import { pilotageApi, type FactChip, type InboxItem } from '@/lib/api';
import { adminOrderHref } from '@/lib/orderHref';
import { usePilotage } from './usePilotage';
import { displayName, groupByClient, latestIso, shortcutFor, splitByPeriod, stepIndex, waitLabel, type ClientRow } from './workbench';
import type { DateRange } from '@/components/admin/DateRangePicker';
import { SwipeRow } from './SwipeRow';
import { useIsPhone } from './phone';
import { SnoozeMenu } from './ChatInboxBar';
// The person's own pin + private name on a client's row (2026-10-21). The
// Inbox is ranked by urgency, so a pin marks the row but does not move it.
import { useChatPrefs } from './chat/useChatPrefs';
import { PrefsName } from './chat/ChatPrefsControls';

/** No period given = everything (the Inbox was always whole before the timeline). */
const ALL_TIME: DateRange = {};
import { PaymentProposalsCard } from './PaymentProposalsCard';
import { OpenLoopsCard } from './OpenLoopsCard';
import { ClientPromisesCard } from './ClientPromisesCard';
import { ReactionsCard } from './ReactionsCard';
import { WelcomeAbCard } from './WelcomeAbCard';

const MAD = (n: number) => new Intl.NumberFormat('fr-FR').format(Math.round(n));

const TIER_STYLE = {
  now: { dot: 'bg-error', text: 'text-error', ring: 'border-l-error' },
  today: { dot: 'bg-warning', text: 'text-[#A8743A] dark:text-status-waiting', ring: 'border-l-warning' },
  old: { dot: 'bg-border-strong', text: 'text-text-tertiary', ring: 'border-l-border-strong' },
} as const;

/** A fact, as a chip: ✓ done · — to do · ! gap · i information. Never colour alone. */
export function FactChips({ chips, compact = false }: { chips: FactChip[]; compact?: boolean }) {
  const { t } = useT();
  if (!chips.length) return null;
  const mark = { done: '✓', todo: '—', warn: '!', info: 'i' } as const;
  const tone = {
    done: 'border-success/30 bg-success/10 text-[#56654C] dark:text-status-done',
    todo: 'border-border-subtle bg-bg-card text-text-tertiary',
    warn: 'border-warning/40 bg-warning/10 text-[#8F5F2A] dark:text-status-waiting',
    info: 'border-info/30 bg-info/10 text-[#4E5F6B] dark:text-adm-ink-2',
  } as const;
  return (
    <ul className={`flex flex-wrap ${compact ? 'gap-1' : 'gap-1.5'}`} aria-label={t('Faits')}>
      {chips.map((c) => (
        <li
          key={c.key}
          className={`inline-flex items-center gap-1 rounded-full border ${compact ? 'px-1.5 py-px text-[10.5px]' : 'px-2 py-0.5 text-[11.5px]'} ${tone[c.state]}`}
        >
          <span aria-hidden className="font-semibold">{mark[c.state]}</span>
          <span>{t(c.label)}</span>
        </li>
      ))}
    </ul>
  );
}

function sinceText(iso: string, lang: 'fr' | 'en'): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  return d.toLocaleString(lang === 'en' ? 'en-GB' : 'fr-FR', {
    timeZone: 'Africa/Casablanca', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

type Done = { label: string; name: string; items: InboxItem[] };
type Act = { items: InboxItem[]; action: 'handled' | 'ignored' | 'snoozed'; note?: string; snoozeUntil?: string };

const TIER_LABEL = { now: 'À traiter maintenant', today: 'Aujourd’hui', old: 'Plus anciens que 7 jours' } as const;

/** Where the closed system-alert lines are kept (this browser only). */
const CLOSED_SYSTEM_KEY = 'storiz.admin.inboxSystemClosed';
const systemId = (s: { kind: string; sinceIso: string }) => `${s.kind}|${s.sinceIso}`;
function readClosedSystem(): Set<string> {
  try {
    const v = JSON.parse(localStorage.getItem(CLOSED_SYSTEM_KEY) ?? '[]');
    return new Set(Array.isArray(v) ? v.map(String) : []);
  } catch {
    return new Set();
  }
}

export function InboxTab({ onOpenChat, range = ALL_TIME, onShowAll }: {
  onOpenChat: (chatId: string) => void;
  /** The page's timeline (owner 2026-09-26: 7 days by default). */
  range?: DateRange;
  onShowAll?: () => void;
}) {
  const { t, lang } = useT();
  const can = useCan();
  const qc = useQueryClient();
  const { data, isLoading, isError } = usePilotage();
  const isPhone = useIsPhone();
  const chatPrefs = useChatPrefs();
  // On a phone the client's panel is a sheet, opened by a tap (desktop: always beside the list).
  const [sheetOpen, setSheetOpen] = useState(false);

  // Items the manager just dealt with leave at once — the next refresh agrees.
  const [gone, setGone] = useState<Set<string>>(() => new Set());
  // The red system banner, closed line by line (owner, 2026-10-10). Kept in
  // this browser by kind + time: the same problem raised again later comes back.
  const [closedSystem, setClosedSystem] = useState<Set<string>>(() => readClosedSystem());
  const closeSystem = (id: string) => setClosedSystem((prev) => {
    const next = new Set(prev).add(id);
    try { localStorage.setItem(CLOSED_SYSTEM_KEY, JSON.stringify([...next].slice(-50))); } catch { /* closed for this visit only */ }
    return next;
  });
  const [selKey, setSelKey] = useState<string | null>(null);
  const [panel, setPanel] = useState<'none' | 'ignore' | 'snooze'>('none');
  const [ignoreNote, setIgnoreNote] = useState('');
  const [help, setHelp] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [lastDone, setLastDone] = useState<Done | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showOld, setShowOld] = useState(false);
  const [bulkNote, setBulkNote] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const noteRef = useRef<HTMLInputElement>(null);

  const inbox = data?.inbox;
  // ⚠️ THE PERIOD PICKS CLIENTS, not items: a client ACTIVE in it (their
  // latest activity) shows ALL their open items — an older complaint included.
  // The rest is counted and offered back (« Tout afficher »), never dropped.
  const grouped = useMemo(() => groupByClient((inbox?.items ?? []).filter((i) => !gone.has(i.key))), [inbox, gone]);
  const period = useMemo(
    () => splitByPeriod(grouped, (r) => latestIso(r.items.map((i) => i.activityIso)), range),
    [grouped, range],
  );
  const rows = period.shown;
  const outsideClients = period.outside;
  const nowRows = rows.filter((r) => r.tier === 'now');
  const todayRows = rows.filter((r) => r.tier === 'today');
  const oldRows = rows.filter((r) => r.tier === 'old');
  // The older group joins the keyboard walk only when it is open.
  const ordered = useMemo(() => [...nowRows, ...todayRows, ...(showOld ? oldRows : [])], [nowRows, todayRows, oldRows, showOld]);
  const selIndex = ordered.findIndex((r) => r.key === selKey);
  const selected = selIndex >= 0 ? ordered[selIndex] : null;

  useEffect(() => {
    if (!selected && ordered.length) setSelKey(ordered[0].key);
  }, [selected, ordered]);

  // A server refresh that no longer lists a « gone » key means it agrees.
  useEffect(() => {
    if (!inbox) return;
    const live = new Set(inbox.items.map((i) => i.key));
    setGone((g) => {
      const next = new Set([...g].filter((k) => live.has(k)));
      return next.size === g.size ? g : next;
    });
  }, [inbox]);

  // ✓ / ✗ / snooze cover what THIS screen showed (owner, 2026-10-03): a message
  // that lands after it was never seen, so it stays new (worker lib/inbox.ts).
  const seenAt = data?.generated_at;
  const act = useMutation({
    mutationFn: (p: Act) => pilotageApi.inboxAct(p.items, p.action, { note: p.note, snoozeUntil: p.snoozeUntil, seenAt }),
    onMutate: (p) => {
      setError(null);
      const keys = new Set(p.items.map((i) => i.key));
      const wholeRow = selected ? selected.items.every((i) => keys.has(i.key)) : false;
      if (wholeRow) {
        const next = ordered[selIndex + 1] ?? ordered[selIndex - 1] ?? null;
        setSelKey(next?.key ?? null);
      }
      setGone((g) => { const n = new Set(g); for (const k of keys) n.add(k); return n; });
      setPanel('none');
      setIgnoreNote('');
      setSheetOpen(false);
      const label = p.action === 'handled' ? t('Traité') : p.action === 'ignored' ? t('Ignoré') : t('Rappel programmé');
      setLastDone({ label, name: p.items[0]?.name ?? '', items: p.items });
    },
    // ✓ hands the conversation back to the AI: say so on the undo line, and
    // refresh the chats so « une personne a la main » goes away there too.
    onSuccess: (res, p) => {
      if (p.action !== 'handled' || !res?.resumed?.length) return;
      setLastDone((d) => (d && d.items === p.items ? { ...d, label: t('Traité — l’IA reprend') } : d));
      void qc.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
      void qc.invalidateQueries({ queryKey: ['whatsapp-conversation'] });
    },
    onError: (e, p) => {
      setGone((g) => { const n = new Set(g); for (const i of p.items) n.delete(i.key); return n; });
      setLastDone(null);
      setError((e as Error).message);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['pilotage'] }),
  });

  // ─── A FAILED ANSWER, SENT AGAIN (owner, 2026-10-03) ───────────────────
  // The worker puts the SAME draft back in the queue, once however many clicks
  // (lib/replyRetry.ts); a refusal comes back in plain words and is shown here.
  const [retryNote, setRetryNote] = useState<{ chatId: string; text: string } | null>(null);
  const retry = useMutation({
    mutationFn: (chatId: string) => pilotageApi.retryReply(chatId),
    onMutate: () => setRetryNote(null),
    onSuccess: (_d, chatId) => setRetryNote({ chatId, text: t('Réponse remise dans la file — elle part au prochain passage.') }),
    onError: (e, chatId) => setRetryNote({ chatId, text: (e as Error).message }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['pilotage'] }),
  });

  // « Approve and send » a 3D preview — the same door as his WhatsApp OK (2026-10-08).
  const [approveNote, setApproveNote] = useState<{ id: string; text: string; order?: string; held?: boolean } | null>(null);
  const approve = useMutation({
    mutationFn: (shopifyOrderId: string) => pilotageApi.approvePreview(shopifyOrderId),
    onMutate: () => setApproveNote(null),
    // What happens next, as the worker judged it — never « 5 minutes » at night (2026-10-15).
    onSuccess: (d, id) => {
      const plan = d.sends ? (d as PreviewPlanLike) : null;
      setApproveNote({ id, order: d.order, held: planIsHeld(plan), text: `${t('Approuvé —')} ${previewPlanText(plan, t)}` });
    },
    onError: (e, id) => setApproveNote({ id, text: (e as Error).message }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['pilotage'] }),
  });

  const undo = useMutation({
    mutationFn: async (d: Done) => { for (const i of d.items) await pilotageApi.inboxUndo(i.key); },
    onMutate: (d) => {
      setGone((g) => { const n = new Set(g); for (const i of d.items) n.delete(i.key); return n; });
      setLastDone(null);
    },
    onError: (e) => setError((e as Error).message),
    // Undo of a ✓ gives the chat back to the person — the chats show it.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
      void qc.invalidateQueries({ queryKey: ['whatsapp-conversation'] });
      return qc.invalidateQueries({ queryKey: ['pilotage'] });
    },
  });

  // The undo line stays six seconds — long enough to catch a slip.
  useEffect(() => {
    if (!lastDone) return;
    const id = window.setTimeout(() => setLastDone(null), 6000);
    return () => window.clearTimeout(id);
  }, [lastDone]);

  const doHandle = useCallback(() => {
    if (selected && can('inbox.handle')) act.mutate({ items: selected.items, action: 'handled' });
  }, [selected, can, act]);
  const openIgnore = useCallback(() => {
    if (!selected || !can('inbox.ignore')) return;
    setPanel('ignore');
    window.setTimeout(() => noteRef.current?.focus(), 0);
  }, [selected, can]);
  const openChat = useCallback(() => {
    if (selected?.chatId) onOpenChat(selected.chatId);
  }, [selected, onOpenChat]);

  // ─── KEYBOARD ─────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const cut = shortcutFor({
        key: e.key, shiftKey: e.shiftKey, metaKey: e.metaKey, ctrlKey: e.ctrlKey, altKey: e.altKey,
        targetTag: target?.tagName ?? 'BODY', editable: Boolean(target?.isContentEditable),
      });
      if (!cut) return;
      // A focused button handles its own Enter.
      if (cut === 'open' && target?.tagName === 'BUTTON') return;
      e.preventDefault();
      switch (cut) {
        case 'next': setSelKey(ordered[stepIndex(selIndex, ordered.length, 1)]?.key ?? null); setPanel('none'); break;
        case 'prev': setSelKey(ordered[stepIndex(selIndex, ordered.length, -1)]?.key ?? null); setPanel('none'); break;
        case 'handle': doHandle(); break;
        case 'ignore': openIgnore(); break;
        case 'snooze': if (selected && can('inbox.snooze')) setPanel('snooze'); break;
        case 'open': openChat(); break;
        case 'undo': if (lastDone && can('inbox.undo')) undo.mutate(lastDone); break;
        case 'help': setHelp((h) => !h); break;
        case 'close': setPanel('none'); setHelp(false); setLogOpen(false); setSheetOpen(false); (target as HTMLElement | null)?.blur?.(); break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ordered, selIndex, selected, doHandle, openIgnore, openChat, lastDone, can, undo]);

  if (isLoading) return <p className="text-[12.5px] text-text-tertiary">{t('Chargement…')}</p>;
  if (isError || !data) return <p className="text-[12.5px] text-[#8F5F2A] dark:text-status-waiting">{t('Impossible de charger l’Inbox.')}</p>;
  if (!inbox) return <p className="text-[12.5px] text-text-tertiary">{t('L’Inbox arrive avec la nouvelle version du serveur.')}</p>;

  const hiddenTotal = inbox.hidden.handled + inbox.hidden.ignored + inbox.hidden.snoozed;
  const oldItems = oldRows.flatMap((r) => r.items);

  const rowList = (list: ClientRow[]) => (
    <ul className="rounded-xl border border-border-subtle bg-bg-card divide-y divide-border-subtle overflow-hidden">
      {list.map((r) => {
        const active = r.key === selKey;
        const button = (
            <button
              type="button"
              role="option"
              aria-selected={active}
              onClick={() => { setSelKey(r.key); setPanel('none'); if (isPhone) setSheetOpen(true); }}
              onDoubleClick={() => r.chatId && onOpenChat(r.chatId)}
              className={`w-full text-left px-3.5 py-3 md:py-2.5 border-l-[3px] transition-colors duration-150 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500 ${
                active ? `bg-accent-soft/60 ${TIER_STYLE[r.tier].ring}` : 'border-l-transparent hover:bg-bg-elevated'
              }`}
            >
              <span className="flex items-baseline gap-2 min-w-0">
                <PrefsName prefs={chatPrefs} chatId={r.chatId} name={t(displayName(r.name))} className="font-semibold text-[13px] text-text-primary truncate" />
                {r.orderName && <span className="text-[11.5px] text-text-tertiary shrink-0 tabular-nums">{r.orderName}</span>}
                <span className="ml-auto flex items-baseline gap-2 shrink-0">
                  {r.money > 0 && <span className="text-[11.5px] text-text-secondary tabular-nums">{MAD(r.money)} MAD</span>}
                  <span className={`text-[11.5px] tabular-nums ${r.overdue && r.tier === 'now' ? 'text-error font-semibold' : 'text-text-tertiary'}`}>
                    {waitLabel(r.ageMin)}
                  </span>
                </span>
              </span>
              <span className="flex items-center gap-1.5 mt-0.5 min-w-0">
                <span className="text-[12px] text-text-secondary truncate">{t(r.items[0].title)}</span>
                {r.items.length > 1 && (
                  <span className="text-[11px] text-text-tertiary shrink-0">{t('+ {n} autre(s)', { n: r.items.length - 1 })}</span>
                )}
              </span>
            </button>
        );
        return (
          <li key={r.key}>
            {/* A phone swipes: right = handled, left = ignore — the same act() as the buttons, undoable. */}
            {isPhone ? (
              <SwipeRow
                allow={{ handle: can('inbox.handle'), ignore: can('inbox.ignore') }}
                onSwipe={(action) => act.mutate({ items: r.items, action })}
              >
                {button}
              </SwipeRow>
            ) : button}
          </li>
        );
      })}
    </ul>
  );

  const systemShown = inbox.system.filter((s) => !closedSystem.has(systemId(s)));

  return (
    <div className="space-y-4">
      {/* The system itself: a disconnected line or empty AI credits is everybody's problem. */}
      {systemShown.length > 0 && (
        <div role="alert" className="rounded-xl border border-error/30 bg-error/10 px-4 py-3 text-[12.5px] text-error space-y-1">
          {systemShown.map((s) => (
            <div key={s.kind} className="flex items-start gap-2">
              <p className="break-words flex-1 min-w-0"><b className="font-semibold">{t(s.title)}</b> · {sinceText(s.sinceIso, lang)}</p>
              <button type="button" onClick={() => closeSystem(systemId(s))} aria-label={t('Masquer cette alerte')} title={t('Masquer cette alerte')}
                className="shrink-0 -my-0.5 h-6 w-6 rounded-full flex items-center justify-center text-error/80 hover:text-error hover:bg-error/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-error/40">
                <span aria-hidden className="text-[15px] leading-none">×</span>
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-[22px] leading-tight text-text-primary">{t('À traiter')}</h2>
          {/* On a phone the numbers strip above says this; desktop keeps its line. */}
          <p className="hidden md:block text-[12px] text-text-secondary mt-0.5 tabular-nums">
            {rows.length === 0
              ? (outsideClients > 0 ? t('Rien dans cette période.') : t('Rien n’attend une personne.'))
              : t('{a} client(s) maintenant · {b} aujourd’hui · {c} plus anciens', { a: nowRows.length, b: todayRows.length, c: oldRows.length })}
          </p>
          {outsideClients > 0 && (
            <p className="text-[11.5px] text-text-tertiary mt-0.5 tabular-nums">
              {t('{n} client(s) hors de cette période', { n: outsideClients })}
              {onShowAll && (<>
                {' · '}
                <button type="button" onClick={onShowAll} className="underline underline-offset-2 hover:text-text-primary cursor-pointer">
                  {t('Tout afficher')}
                </button>
              </>)}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3 text-[11.5px] text-text-tertiary">
          {hiddenTotal > 0 && (
            <span>{t('{h} traités · {i} ignorés · {s} en rappel', { h: inbox.hidden.handled, i: inbox.hidden.ignored, s: inbox.hidden.snoozed })}</span>
          )}
          <button type="button" onClick={() => setLogOpen(true)} className="min-h-[44px] md:min-h-0 underline underline-offset-2 hover:text-text-primary cursor-pointer">
            {t('Journal')}
          </button>
          <button type="button" onClick={() => setHelp(true)} className="hidden md:inline underline underline-offset-2 hover:text-text-primary cursor-pointer">
            {t('Raccourcis (?)')}
          </button>
        </div>
      </div>

      {error && <p role="alert" className="text-[12px] text-error">{error}</p>}
      {/* The approved item leaves the list — what happens next stays in sight (2026-10-15). */}
      {approveNote?.order && (
        <p role="status" className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-[12.5px] ${approveNote.held ? 'border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:text-amber-200' : 'border-green-200 bg-green-50 text-green-900 dark:bg-green-900/20 dark:text-green-200'}`}>
          <span className="flex-1">{t('Aperçu')} {approveNote.order} — {approveNote.text}</span>
          <button type="button" onClick={() => setApproveNote(null)} className="shrink-0 underline underline-offset-2 cursor-pointer">{t('OK')}</button>
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] gap-4 items-start">
        {/* ─── THE LIST ─────────────────────────────────────────────────── */}
        <div className="space-y-4" role="listbox" aria-label={t('Clients à traiter')}>
          {rows.length === 0 && (
            <div className="rounded-xl border border-dashed border-border-default bg-bg-card px-6 py-10 text-center">
              {outsideClients > 0 ? (<>
                {/* ⚠️ NOT « all clear »: people are still waiting, just before the period. */}
                <p className="font-heading text-[17px] text-text-primary">{t('Rien dans cette période.')}</p>
                <p className="text-[12px] text-text-secondary mt-1">
                  {t('{n} client(s) plus anciens attendent encore.', { n: outsideClients })}
                  {onShowAll && (<>
                    {' '}
                    <button type="button" onClick={onShowAll} className="underline underline-offset-2 hover:text-text-primary cursor-pointer">
                      {t('Tout afficher')}
                    </button>
                  </>)}
                </p>
              </>) : (<>
                <p className="font-heading text-[17px] text-text-primary">{t('Tout est à jour.')}</p>
                <p className="text-[12px] text-text-secondary mt-1">{t('Un client sans réponse depuis 10 minutes apparaîtra ici tout seul.')}</p>
              </>)}
            </div>
          )}
          {([['now', nowRows], ['today', todayRows]] as const).map(([tier, list]) => list.length > 0 && (
            <section key={tier}>
              <h3 className={`flex items-center gap-2 text-[12px] font-semibold mb-1.5 ${TIER_STYLE[tier].text}`}>
                <span className={`w-2 h-2 rounded-full ${TIER_STYLE[tier].dot}`} aria-hidden />
                {t(TIER_LABEL[tier])} · {list.length}
              </h3>
              {rowList(list)}
            </section>
          ))}
          {oldRows.length > 0 && (
            <section>
              <div className="rounded-xl border border-dashed border-border-default bg-bg-card px-4 py-3 space-y-2">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-[12.5px] text-text-secondary">
                    <b className="font-semibold text-text-primary">{t(TIER_LABEL.old)} · {oldRows.length}</b>{' — '}
                    {t('souvent d’avant que l’agent réponde à tout le monde.')}
                  </p>
                  <span className="flex gap-3 text-[12px]">
                    <button type="button" onClick={() => setShowOld((v) => !v)} aria-expanded={showOld}
                      className="underline underline-offset-2 text-text-secondary hover:text-text-primary cursor-pointer">
                      {showOld ? t('Masquer') : t('Afficher')}
                    </button>
                    {can('inbox.ignore') && (
                      <button type="button" onClick={() => setBulkOpen((v) => !v)} aria-expanded={bulkOpen}
                        className="underline underline-offset-2 text-text-secondary hover:text-text-primary cursor-pointer">
                        {t('Tout ignorer…')}
                      </button>
                    )}
                  </span>
                </div>
                {bulkOpen && (
                  <form className="flex flex-wrap gap-2 items-center"
                    onSubmit={(e) => { e.preventDefault(); act.mutate({ items: oldItems, action: 'ignored', note: bulkNote }); setBulkOpen(false); setBulkNote(''); }}>
                    <label htmlFor="bulk-note" className="sr-only">{t('Raison')}</label>
                    <input id="bulk-note" value={bulkNote} onChange={(e) => setBulkNote(e.target.value)} maxLength={500}
                      placeholder={t('Raison (ex. avant l’ouverture de l’agent à tous)')}
                      className="flex-1 min-w-[220px] h-9 px-3 rounded-lg border border-border-default bg-bg-card text-[12.5px] focus:outline-none focus:ring-2 focus:ring-brand-500" />
                    <button type="submit" disabled={act.isPending}
                      className="h-9 px-3.5 rounded-lg bg-text-primary text-bg-card text-[12.5px] font-semibold cursor-pointer disabled:opacity-50">
                      {t('Ignorer les {n}', { n: oldItems.length })}
                    </button>
                    <span className="w-full text-[11px] text-text-tertiary">{t('Chacun revient si le client écrit à nouveau. Tout est dans le journal, annulable.')}</span>
                  </form>
                )}
              </div>
              {showOld && <div className="mt-2">{rowList(oldRows)}</div>}
            </section>
          )}
        </div>

        {/* ─── THE PANEL ────────────────────────────────────────────────── */}
        {selected && (!isPhone || sheetOpen) && (<>
          {/* On a phone: a sheet from the bottom over a shade; a tap on the shade closes it. */}
          {isPhone && <div className="fixed inset-0 z-[55] bg-text-primary/30 storiz-fade-in" onClick={() => setSheetOpen(false)} aria-hidden />}
          <aside
            data-inbox-sheet={isPhone ? '' : undefined}
            role={isPhone ? 'dialog' : undefined}
            aria-modal={isPhone ? true : undefined}
            aria-label={isPhone ? t(displayName(selected.name)) : undefined}
            className={isPhone
              ? 'fixed inset-x-0 bottom-0 z-[56] max-h-[85dvh] overflow-y-auto overscroll-contain rounded-t-2xl border-t border-border-subtle bg-bg-card shadow-xl wb-row-in'
              : 'lg:sticky lg:top-4 rounded-xl border border-border-subtle bg-bg-card overflow-hidden'}
            style={isPhone ? { paddingBottom: 'var(--adm-safe-bottom, env(safe-area-inset-bottom, 0px))' } : undefined}
            data-no-ptr={isPhone ? '' : undefined}
            aria-live="polite">
            {isPhone && (
              <div className="sticky top-0 bg-bg-card pt-2 pb-1 flex justify-center">
                <button type="button" onClick={() => setSheetOpen(false)} aria-label={t('Fermer')}
                  className="h-6 w-16 flex items-center justify-center cursor-pointer">
                  <span className="h-1 w-10 rounded-full bg-border-strong" aria-hidden />
                </button>
              </div>
            )}
            <header className="px-5 pt-4 pb-3 border-b border-border-subtle">
              <p className={`flex items-center gap-2 text-[11.5px] font-semibold ${TIER_STYLE[selected.tier].text}`}>
                <span className={`w-2 h-2 rounded-full ${TIER_STYLE[selected.tier].dot}`} aria-hidden />
                {t(TIER_LABEL[selected.tier])} · {t('en attente depuis {w}', { w: waitLabel(selected.ageMin) })}
              </p>
              <h3 className="font-heading text-[20px] leading-tight text-text-primary mt-1">{t(displayName(selected.name))}</h3>
              <p className="text-[12px] text-text-secondary mt-0.5 flex flex-wrap gap-x-2">
                {selected.orderName && (adminOrderHref(selected)
                  ? <a href={adminOrderHref(selected)!} className="underline underline-offset-2 hover:text-text-primary">{selected.orderName}</a>
                  : <span>{selected.orderName}</span>)}
                {selected.stageLabel && <span>· {t(selected.stageLabel)}</span>}
                {selected.money > 0 && <span className="tabular-nums">· {MAD(selected.money)} MAD</span>}
              </p>
            </header>

            <div className="px-5 py-4 space-y-4">
              {selected.chips.length > 0 && <FactChips chips={selected.chips} />}

              {/* Why she is here — every open item, with its own clock and buttons. */}
              <div>
                <p className="text-[12px] font-semibold text-text-tertiary mb-1.5">{t('Pourquoi c’est ici')}</p>
                <ul className="space-y-2">
                  {selected.items.map((i) => (
                    <li key={i.key} className="rounded-lg border border-border-subtle px-3 py-2.5">
                      <p className="flex items-center gap-2 text-[11.5px]">
                        <span className={`w-1.5 h-1.5 rounded-full ${TIER_STYLE[i.tier].dot}`} aria-hidden />
                        <span className={`font-semibold ${TIER_STYLE[i.tier].text}`}>{t(i.reasonLabel)}</span>
                        <span className={`ml-auto tabular-nums ${i.overdue && i.tier === 'now' ? 'text-error' : 'text-text-tertiary'}`}>
                          {sinceText(i.sinceIso, lang)}
                        </span>
                      </p>
                      <p className="text-[13.5px] text-text-primary mt-1 leading-snug">{t(i.title)}</p>
                      {i.detail && i.detail !== i.wants && <p className="text-[12.5px] text-text-secondary mt-0.5 leading-snug break-words">{i.detail}</p>}
                      {/* « Every item says what the client wants, and what I should do » (2026-10-07). */}
                      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 text-[12.5px] leading-snug">
                        {i.wants && (<>
                          <dt className="font-semibold text-text-tertiary">{t('Elle veut')}</dt>
                          <dd className="text-text-primary break-words">{i.wants}</dd>
                        </>)}
                        <dt className="font-semibold text-text-tertiary">{t('À faire')}</dt>
                        <dd className="text-text-primary font-medium break-words">{t(i.todo ?? '')}</dd>
                      </dl>
                      {i.retryChatId && (
                        <p className="flex flex-wrap items-baseline gap-x-2 mt-1.5 text-[11.5px]">
                          <button type="button" disabled={!can('inbox.handle') || retry.isPending}
                            onClick={() => retry.mutate(i.retryChatId!)}
                            className="text-brand-600 dark:text-brand-300 underline underline-offset-2 cursor-pointer disabled:opacity-40">
                            ↻ {t('Renvoyer la réponse')}
                          </button>
                          {retryNote?.chatId === i.retryChatId && <span className="text-text-secondary">{retryNote.text}</span>}
                        </p>
                      )}
                      {i.approvePreviewFor && (
                        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1.5 text-[11.5px]">
                          <button type="button" disabled={!can('inbox.handle') || approve.isPending}
                            onClick={() => approve.mutate(i.approvePreviewFor!)}
                            className="h-8 px-3 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-[12px] font-semibold cursor-pointer disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
                            ✓ {t('Approuver et envoyer')}
                          </button>
                          {approveNote?.id === i.approvePreviewFor && <span className="text-text-secondary">{approveNote.text}</span>}
                        </p>
                      )}
                      {selected.items.length > 1 && (
                        <p className="flex gap-3 mt-1.5 text-[11.5px]">
                          <button type="button" disabled={!can('inbox.handle')} onClick={() => act.mutate({ items: [i], action: 'handled' })}
                            className="text-[#56654C] dark:text-status-done underline underline-offset-2 cursor-pointer disabled:opacity-40">✓ {t('Traité')}</button>
                          <button type="button" disabled={!can('inbox.ignore')} onClick={() => act.mutate({ items: [i], action: 'ignored' })}
                            className="text-text-secondary underline underline-offset-2 cursor-pointer disabled:opacity-40">✗ {t('Ignorer')}</button>
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              {/* One primary action; the others are quieter. They act on the whole client. */}
              <div className="flex flex-wrap gap-2 pt-1">
                {selected.chatId && (
                  <button type="button" onClick={openChat}
                    className="h-10 px-4 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-[13px] font-semibold cursor-pointer transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-500">
                    {t('Ouvrir la conversation')} <kbd className="hidden md:inline ml-1 text-[10px] opacity-75">↵</kbd>
                  </button>
                )}
                <button type="button" onClick={doHandle} disabled={!can('inbox.handle') || act.isPending}
                  className="h-10 px-3.5 rounded-lg border border-success/40 bg-success/10 text-[#56654C] dark:text-status-done text-[13px] font-semibold cursor-pointer hover:bg-success/20 transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
                  ✓ {selected.items.length > 1 ? t('Tout traité') : t('Traité')} <kbd className="hidden md:inline ml-1 text-[10px] opacity-60">E</kbd>
                </button>
                <button type="button" onClick={openIgnore} disabled={!can('inbox.ignore')}
                  className="h-10 px-3.5 rounded-lg border border-border-default bg-bg-card text-text-secondary text-[13px] cursor-pointer hover:bg-bg-elevated transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
                  ✗ {t('Ignorer')} <kbd className="hidden md:inline ml-1 text-[10px] opacity-60">#</kbd>
                </button>
                <button type="button" onClick={() => setPanel(panel === 'snooze' ? 'none' : 'snooze')} disabled={!can('inbox.snooze')}
                  aria-expanded={panel === 'snooze'}
                  className="h-10 px-3.5 rounded-lg border border-border-default bg-bg-card text-text-secondary text-[13px] cursor-pointer hover:bg-bg-elevated transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
                  {t('Rappeler')} <kbd className="hidden md:inline ml-1 text-[10px] opacity-60">S</kbd>
                </button>
              </div>

              {panel === 'ignore' && (
                <form
                  className="rounded-lg border border-border-subtle bg-bg-elevated/60 p-3 space-y-2"
                  onSubmit={(e) => { e.preventDefault(); act.mutate({ items: selected.items, action: 'ignored', note: ignoreNote }); }}
                >
                  <label htmlFor="ignore-note" className="block text-[11.5px] font-semibold text-text-secondary">
                    {t('Pourquoi ? (facultatif — sert à créer des règles)')}
                  </label>
                  <input
                    id="ignore-note" ref={noteRef} value={ignoreNote} onChange={(e) => setIgnoreNote(e.target.value)} maxLength={500}
                    placeholder={t('Ex. pas un client, message automatique…')}
                    className="w-full h-10 px-3 rounded-lg border border-border-default bg-bg-card text-[13px] text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                  <p className="text-[11px] text-text-tertiary">{t('Il revient si le client écrit à nouveau. Pour ne plus relancer ce client du tout, utilisez « Ignorer le client » sur sa commande.')}</p>
                  <div className="flex gap-2">
                    <button type="submit" className="h-9 px-3.5 rounded-lg bg-text-primary text-bg-card text-[12.5px] font-semibold cursor-pointer">
                      {selected.items.length > 1 ? t('Ignorer les {n}', { n: selected.items.length }) : t('Ignorer cet élément')}
                    </button>
                    <button type="button" onClick={() => setPanel('none')} className="h-9 px-3 text-[12.5px] text-text-secondary cursor-pointer">{t('Annuler')}</button>
                  </div>
                </form>
              )}

              {panel === 'snooze' && (
                <div className="rounded-lg border border-border-subtle bg-bg-elevated/60 p-3">
                  <SnoozeMenu onPick={(until) => act.mutate({ items: selected.items, action: 'snoozed', snoozeUntil: until })} />
                </div>
              )}
            </div>
          </aside>
        </>)}
      </div>

      {/* The follow-ups the Inbox does not compute itself — money first. */}
      <section className="space-y-3 pt-2">
        <h3 className="font-heading text-[17px] text-text-primary">{t('Autres suivis')}</h3>
        <PaymentProposalsCard />
        <OpenLoopsCard />
        <ClientPromisesCard />
        <ReactionsCard />
        <WelcomeAbCard />
      </section>

      {/* ─── UNDO LINE ──────────────────────────────────────────────────── */}
      {lastDone && (
        <div role="status"
          // Above the shell's bottom bar on a phone (0 on desktop): the shared contract.
          style={{ bottom: 'calc(var(--adm-bottom-h, 0px) + 20px)' }}
          className="fixed left-1/2 -translate-x-1/2 z-[57] wb-row-in flex w-max max-w-[calc(100vw-32px)] items-center gap-3 rounded-full bg-text-primary text-bg-card px-4 py-2.5 shadow-lg text-[12.5px]">
          <span className="min-w-0 truncate whitespace-nowrap">
            {lastDone.label} — {lastDone.items.length > 1 ? t('{n} éléments', { n: lastDone.items.length }) : t(displayName(lastDone.name))}
          </span>
          {can('inbox.undo') && (
            <button type="button" onClick={() => undo.mutate(lastDone)} className="min-h-[36px] px-1 font-semibold text-brand-300 hover:text-brand-200 cursor-pointer">
              <span className="whitespace-nowrap">{t('Annuler')}<kbd className="hidden md:inline ml-1 text-[10px] opacity-70">Z</kbd></span>
            </button>
          )}
        </div>
      )}

      {help && <ShortcutHelp onClose={() => setHelp(false)} />}
      {logOpen && <InboxLog onClose={() => setLogOpen(false)} />}
    </div>
  );
}

function ShortcutHelp({ onClose }: { onClose: () => void }) {
  const { t } = useT();
  const rows: Array<[string, string]> = [
    ['J / K', t('Suivant / précédent')], ['E', t('Traité')], ['#', t('Ignorer')], ['S', t('Rappeler plus tard')],
    ['↵', t('Ouvrir la conversation')], ['Z', t('Annuler la dernière action')], ['?', t('Cette aide')], ['Esc', t('Fermer')],
  ];
  return (
    <div className="fixed inset-0 z-50 bg-text-primary/30 flex items-center justify-center p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={t('Raccourcis clavier')}
        className="w-full max-w-sm rounded-2xl bg-bg-card border border-border-subtle p-5 storiz-fade-in" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-[18px] text-text-primary mb-3">{t('Raccourcis clavier')}</h3>
        <dl className="grid grid-cols-[72px_1fr] gap-y-2 text-[13px]">
          {rows.map(([k, v]) => (
            <div key={k} className="contents">
              <dt><kbd className="px-1.5 py-0.5 rounded border border-border-default bg-bg-elevated text-[11.5px] text-text-primary">{k}</kbd></dt>
              <dd className="text-text-secondary">{v}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

function InboxLog({ onClose }: { onClose: () => void }) {
  const { t, lang } = useT();
  const can = useCan();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['pilotage', 'inbox-log'], queryFn: () => pilotageApi.inboxLog() });
  const undo = useMutation({
    mutationFn: (key: string) => pilotageApi.inboxUndo(key),
    onSettled: () => qc.invalidateQueries({ queryKey: ['pilotage'] }),
  });
  const verb = { handled: t('a traité'), ignored: t('a ignoré'), snoozed: t('a mis en rappel'), undone: t('a annulé') } as const;
  return (
    <div className="fixed inset-0 z-50 bg-text-primary/30 flex justify-end" onClick={onClose}>
      <aside role="dialog" aria-modal="true" aria-label={t('Journal')}
        className="w-full max-w-md h-full bg-bg-card border-l border-border-subtle overflow-y-auto storiz-fade-in" onClick={(e) => e.stopPropagation()}>
        <header className="sticky top-0 bg-bg-card px-5 py-4 border-b border-border-subtle flex items-center justify-between">
          <h3 className="font-heading text-[18px] text-text-primary">{t('Journal')}</h3>
          <button type="button" onClick={onClose} className="text-[12.5px] text-text-secondary cursor-pointer">{t('Fermer')}</button>
        </header>
        {isLoading && <p className="px-5 py-4 text-[12.5px] text-text-tertiary">{t('Chargement…')}</p>}
        <ul className="divide-y divide-border-subtle">
          {(data?.items ?? []).map((r) => (
            <li key={r.id} className="px-5 py-3 text-[12.5px]">
              <p className="text-text-primary">
                <b className="font-semibold">{r.acted_by ?? t('quelqu’un')}</b> {verb[r.action]} · {t(r.reasonLabel)}
              </p>
              <p className="text-text-secondary">
                {r.name ?? '—'}
                {r.orderName && (adminOrderHref(r)
                  ? <> · <a href={adminOrderHref(r)!} className="underline underline-offset-2 hover:text-text-primary">{r.orderName}</a></>
                  : ` · ${r.orderName}`)}
                {r.note ? ` — « ${r.note} »` : ''}
              </p>
              <p className="text-[11px] text-text-tertiary mt-0.5 flex items-center gap-3">
                {new Date(r.acted_at).toLocaleString(lang === 'en' ? 'en-GB' : 'fr-FR', { timeZone: 'Africa/Casablanca' })}
                {r.action !== 'undone' && can('inbox.undo') && (
                  <button type="button" onClick={() => undo.mutate(r.item_key)} className="underline underline-offset-2 cursor-pointer hover:text-text-primary">
                    {t('Annuler')}
                  </button>
                )}
              </p>
            </li>
          ))}
          {!isLoading && !(data?.items ?? []).length && <li className="px-5 py-6 text-[12.5px] text-text-tertiary">{t('Rien pour le moment.')}</li>}
        </ul>
      </aside>
    </div>
  );
}
